/**
 * Shared LlamaOne print-document personality (internal ops docs: registro, transferencia).
 * Not for sales tickets or labels.
 */

import { colors } from '../styles/theme/colors';
import { PRIMARY_CTA_GRADIENT } from '../styles/theme/gradients';
import {
  buildPrintChromeHtml,
  escapeHtml,
  openPrintWindow,
  PRINT_NO_PRINT_CSS,
  PRINT_POPUP_BLOCKED_MESSAGE,
  type OpenPrintWindowOptions,
} from './printWindow';

export { escapeHtml } from './printWindow';

/** LlamaOne CTA accent (cyan → emerald) — from theme. */
export const PRINT_DOC_ACCENT = PRIMARY_CTA_GRADIENT;
export const PRINT_DOC_CYAN = colors.info.main;
export const PRINT_DOC_EMERALD = colors.secondary.main;
export const PRINT_DOC_INK = colors.brand.ink;

/** Shared CSS for operational document prints. */
export const PRINT_DOCUMENT_CSS = `
  * { box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    margin: 0;
    padding: 28px 32px;
    background: #fff;
    color: #1f2937;
    line-height: 1.45;
  }
  .doc-header {
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e5e7eb;
    position: relative;
  }
  .doc-header::before {
    content: '';
    display: block;
    height: 3px;
    width: 72px;
    border-radius: 2px;
    background: ${PRINT_DOC_ACCENT};
    margin-bottom: 14px;
  }
  .doc-header-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
  }
  .doc-title {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: ${PRINT_DOC_INK};
    letter-spacing: -0.02em;
  }
  .doc-subtitle {
    margin: 6px 0 0;
    font-size: 13px;
    color: #6b7280;
    font-weight: 500;
  }
  .info-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 18px 20px;
    margin-bottom: 20px;
  }
  .info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px 24px;
  }
  .info-grid-full {
    grid-column: 1 / -1;
  }
  .info-label {
    font-size: 11px;
    color: #6b7280;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 4px;
  }
  .info-value {
    font-size: 14px;
    color: #111827;
    font-weight: 600;
  }
  .info-notes {
    font-size: 13px;
    color: #374151;
    font-weight: 400;
    white-space: pre-wrap;
    margin-top: 4px;
  }
  .status-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
  }
  .status-completado {
    background: #d1fae5;
    color: #065f46;
  }
  .status-cancelado {
    background: #fee2e2;
    color: #991b1b;
  }
  .status-pendiente {
    background: #dbeafe;
    color: #1e40af;
  }
  .section {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 20px;
  }
  .section-header {
    background: #f9fafb;
    padding: 12px 20px;
    border-bottom: 1px solid #e5e7eb;
    font-weight: 600;
    font-size: 13px;
    color: #374151;
    letter-spacing: 0.02em;
  }
  .product-item {
    padding: 14px 20px;
    border-bottom: 1px solid #f3f4f6;
  }
  .product-item:last-child {
    border-bottom: none;
  }
  .product-name {
    font-weight: 600;
    font-size: 14px;
    color: #111827;
    margin-bottom: 2px;
  }
  .product-code {
    font-size: 12px;
    color: #6b7280;
  }
  .product-quantity {
    display: inline-block;
    background: #f3f4f6;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    margin-top: 8px;
  }
  .product-details {
    margin-top: 12px;
    padding: 12px;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
    border-radius: 6px;
  }
  .product-details-title {
    font-size: 11px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
    margin-top: 4px;
  }
  th, td {
    padding: 8px 10px;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
  }
  th {
    background: #f9fafb;
    font-weight: 600;
    color: #6b7280;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
  .status-assigned {
    background: #d1fae5;
    color: #065f46;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
  }
  .status-in-transit {
    background: #dbeafe;
    color: #1e40af;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
  }
  .status-unassigned {
    background: #f3f4f6;
    color: #374151;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
  }
  .total-section {
    background: #f9fafb;
    padding: 14px 20px;
    border-top: 1px solid #e5e7eb;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 10px;
  }
  .total-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 14px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
  }
  .total-label {
    font-size: 10px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .total-value {
    font-size: 14px;
    font-weight: 700;
    color: ${PRINT_DOC_CYAN};
  }
  .total-value-cost {
    color: ${colors.primary.main};
  }
  .total-value-sale {
    color: ${PRINT_DOC_EMERALD};
  }
  .doc-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 16px;
    margin-top: 8px;
    border-top: 1px solid #e5e7eb;
    color: #9ca3af;
    font-size: 11px;
  }
  .doc-footer-brand {
    font-weight: 600;
    color: #6b7280;
    letter-spacing: 0.02em;
  }
  ${PRINT_NO_PRINT_CSS}
  @media print {
    body {
      margin: 0;
      padding: 20px 24px;
    }
    @page {
      margin: 12mm;
      size: auto;
    }
  }
`;

export function buildPrintDocHeader(title: string, subtitle?: string): string {
  const subtitleHtml = subtitle ? `<p class="doc-subtitle">${escapeHtml(subtitle)}</p>` : '';
  return `
    <div class="doc-header">
      <div class="doc-header-row">
        <div>
          <h1 class="doc-title">${escapeHtml(title)}</h1>
          ${subtitleHtml}
        </div>
      </div>
    </div>
  `;
}

export function buildPrintDocInfoField(
  label: string,
  valueHtml: string,
  fullWidth = false,
): string {
  return `
    <div class="${fullWidth ? 'info-grid-full' : ''}">
      <div class="info-label">${escapeHtml(label)}</div>
      <div class="info-value">${valueHtml}</div>
    </div>
  `;
}

export function buildPrintDocFooter(generatedAt: Date = new Date()): string {
  const when = generatedAt.toLocaleString('es-ES');
  return `
    <div class="doc-footer">
      <div>Generado el ${escapeHtml(when)}</div>
      <div class="doc-footer-brand">LlamaOne</div>
    </div>
  `;
}

export interface WrapPrintDocumentOptions {
  /** Browser / print title. */
  documentTitle: string;
  /** Inner body HTML (header, cards, sections, footer). */
  bodyHtml: string;
}

/** Full HTML document with shared document CSS + print chrome. */
export function wrapPrintDocument({ documentTitle, bodyHtml }: WrapPrintDocumentOptions): string {
  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8"/>
    <title>${escapeHtml(documentTitle)}</title>
    <style>${PRINT_DOCUMENT_CSS}</style>
  </head>
  <body>
    ${bodyHtml}
    ${buildPrintChromeHtml()}
  </body>
</html>`;
}

/** Open a document-sized print preview; alerts if popup blocked. */
export function openPrintDocument(
  html: string,
  windowName: string,
  options?: Partial<OpenPrintWindowOptions>,
): Window | null {
  const printWindow = openPrintWindow({
    html,
    size: 'document',
    windowName,
    ...options,
  });
  if (!printWindow) {
    window.alert(PRINT_POPUP_BLOCKED_MESSAGE);
  }
  return printWindow;
}

export function statusBadgeClassFromLabel(status: string): string {
  const s = status?.toLowerCase() || '';
  if (s === 'completed' || s === 'completado' || s === 'recibido') return 'status-completado';
  if (s === 'cancelled' || s === 'cancelado') return 'status-cancelado';
  return 'status-pendiente';
}
